import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import {
  istStornierbar, stornoMitMail, MIN_STORNO_GRUND, NachmeldungPosition, summeAuswahl, auswahlFehler, betragFuerMenge, euro, StornoKandidat,
} from '../../utils/rechnungskorrektur';

// T-165 (v1.8.46, Session 40, 02.10.2026): Rechnung stornieren (Detailansicht) und Nachmeldung abrechnen (Buchungskarte)
// in der Verwaltung, Rechnungen. Die Prüfungen und Berechnungen macht der Server; hier nur Eingabe, Vorschau und Meldungen.

// Review v1.8.46 n12: Anmeldung wie in der übrigen Verwaltung über getAuthHeaders
const authHeader = () => getAuthHeaders();

const fehlertext = (err: any, rueckfall: string): string => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d) return d;
  return d?.errorMessage || d?.ErrorMessage || d?.message || err?.message || rueckfall;
};

/** Bereich "Rechnung stornieren" in der Detailansicht einer Rechnung. */
export const RechnungStornoBereich: React.FC<{
  rechnung: StornoKandidat & { id: number; invoiceNumber: string };
  onStorniert: (hinweis: string) => void;
}> = ({ rechnung, onStorniert }) => {
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (!istStornierbar(rechnung)) return null;

  const stornieren = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await axios.post(`${API_BASE_URL}/invoices/${rechnung.id}/stornieren`, { grund }, { headers: authHeader() });
      setOffen(false);
      setGrund('');
      onStorniert(r.data?.hinweis || `Rechnung ${rechnung.invoiceNumber} ist storniert.`);
    } catch (err: any) {
      setFehler(fehlertext(err, 'Die Rechnung konnte nicht storniert werden.'));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="detail-section" style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
      {!offen ? (
        <button className="btn-secondary" onClick={() => setOffen(true)} title="Nur für Rechnungen ohne Zahlungseingang">
          Rechnung stornieren
        </button>
      ) : (
        <div>
          <h3>Rechnung {rechnung.invoiceNumber} stornieren</h3>
          <p className="section-hint">
            Nur für Rechnungen ohne Zahlungseingang (nach einer Erstattung bitte "Erstattung erfassen" verwenden). Die Rechnung bleibt mit
            dem Vermerk "storniert" erhalten, wartende Mails zu dieser Rechnung werden abgebrochen. Das System erstellt dazu automatisch eine
            Stornorechnung (Nummer ST-..., im Rechnungsarchiv). War die Rechnung schon beim Gast, geht ihm die Stornorechnung in wenigen
            Minuten automatisch per E-Mail zu. Danach kann die Rechnung bei Bedarf neu erstellt werden.
          </p>
          {stornoMitMail(rechnung) && (
            <p className="section-hint" style={{ fontWeight: 600 }}>
              Diese Rechnung ist bereits versendet: Die Stornorechnung geht per E-Mail an den Gast.
            </p>
          )}
          <div className="form-group">
            <label htmlFor="stornoGrund">Grund (Pflicht, mindestens {MIN_STORNO_GRUND} Zeichen)</label>
            <input id="stornoGrund" className="form-input" type="text" maxLength={300} value={grund}
              onChange={(e) => setGrund(e.target.value)} placeholder="z. B. ohne Positionen, wird neu ausgestellt" disabled={laeuft} />
          </div>
          {fehler && <div className="error-message" style={{ whiteSpace: 'pre-line' }}>{fehler}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn-primary" onClick={stornieren} disabled={laeuft || grund.trim().length < MIN_STORNO_GRUND}>
              {laeuft ? 'Wird storniert ...' : 'Jetzt stornieren'}
            </button>
            <button className="btn-secondary" onClick={() => { setOffen(false); setFehler(null); }} disabled={laeuft}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
};

interface Vorschau {
  bookingNumber: string;
  kunde: string;
  fahrtdatum?: string;
  teilnehmer: number;
  moeglich: boolean;
  hinweis: string;
  bisherigeNachtraege: string[];
  positionen: NachmeldungPosition[];
}

/** Dialog "Nachmeldung abrechnen" (Nachtragsrechnung vor der Schlussrechnung). */
export const NachmeldungDialog: React.FC<{
  bookingId: number;
  bookingNumber: string;
  onClose: () => void;
  onErstellt: (hinweis: string) => void;
}> = ({ bookingId, bookingNumber, onClose, onErstellt }) => {
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [laden, setLaden] = useState(true);
  const [mengen, setMengen] = useState<Record<string, number>>({});
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/invoices/booking/${bookingId}/nachmeldung-vorschau`, { headers: authHeader() });
        if (aktiv) setVorschau(r.data);
      } catch (err: any) {
        if (aktiv) setFehler(fehlertext(err, 'Die Vorschau konnte nicht geladen werden.'));
      } finally {
        if (aktiv) setLaden(false);
      }
    })();
    return () => { aktiv = false; };
  }, [bookingId]);

  const positionen = useMemo(() => vorschau?.positionen ?? [], [vorschau]);
  const summe = summeAuswahl(positionen, mengen);
  const eingabeFehler = positionen.length > 0 ? auswahlFehler(positionen, mengen) : null;

  const erstellen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const auswahl = positionen.filter(p => (mengen[p.schluessel] ?? 0) > 0).map(p => ({ schluessel: p.schluessel, menge: mengen[p.schluessel] }));
      const r = await axios.post(`${API_BASE_URL}/invoices/booking/${bookingId}/nachmeldung`, { positionen: auswahl, notiz }, { headers: authHeader() });
      onErstellt(r.data?.hinweis || `Nachtragsrechnung ${r.data?.invoiceNumber ?? ''} erstellt.`);
    } catch (err: any) {
      setFehler(fehlertext(err, 'Die Nachtragsrechnung konnte nicht erstellt werden.'));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !laeuft && onClose()}>
      <div role="dialog" aria-label="Nachmeldung abrechnen" onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', width: 'min(760px, 95vw)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ marginTop: 0 }}>Nachmeldung abrechnen: {bookingNumber}</h2>
        {laden && <p>Vorschau wird geladen ...</p>}
        {vorschau && (
          <>
            <p className="section-hint">
              {vorschau.kunde}{vorschau.fahrtdatum ? `, Fahrt ${new Date(vorschau.fahrtdatum).toLocaleDateString('de-DE')}` : ''}, {vorschau.teilnehmer} Personen
              {vorschau.bisherigeNachtraege.length > 0 ? `. Bisherige Nachtragsrechnungen: ${vorschau.bisherigeNachtraege.join(', ')}` : ''}
            </p>
            <p style={{ whiteSpace: 'pre-line' }}>{vorschau.hinweis}</p>
            {vorschau.moeglich && (
              <>
                <table className="invoices-table" style={{ width: '100%' }}>
                  <thead>
                    <tr><th>Position</th><th>In der Buchung</th><th>Einzelpreis</th><th>Nachgemeldet</th><th>Betrag</th></tr>
                  </thead>
                  <tbody>
                    {positionen.map(p => (
                      <tr key={p.schluessel}>
                        <td>
                          {p.beschreibung}
                          {p.bereitsNachberechnet > 0 ? ` (schon ${p.bereitsNachberechnet} berechnet)` : ''}
                          {p.sperrgrund ? <div className="section-hint">{p.sperrgrund}</div> : null}
                        </td>
                        <td>{p.menge}</td>
                        <td>{euro(p.einzelpreis)}</td>
                        <td>
                          <input type="number" min={0} max={p.maxMenge} step={1} style={{ width: '5rem' }} disabled={laeuft || p.maxMenge === 0}
                            value={mengen[p.schluessel] ?? 0}
                            onChange={(e) => setMengen(prev => ({ ...prev, [p.schluessel]: Number(e.target.value) }))} />
                        </td>
                        <td>{euro(betragFuerMenge(p, mengen[p.schluessel] ?? 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="form-group" style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="nachmeldungNotiz">Notiz (optional, intern)</label>
                  <input id="nachmeldungNotiz" className="form-input" type="text" maxLength={300} value={notiz}
                    onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. telefonisch nachgemeldet am ..." disabled={laeuft} />
                </div>
                <p><strong>Nachtragsrechnung (Zahlbetrag): {euro(summe)}</strong>, fällig in 7 Tagen, wird in der Schlussrechnung verrechnet.
                  Die Rechnung wird erstellt, aber noch nicht versendet (Versand über die Rechnungsliste).</p>
                {eingabeFehler && summe > 0 && <div className="error-message">{eingabeFehler}</div>}
              </>
            )}
          </>
        )}
        {fehler && <div className="error-message" style={{ whiteSpace: 'pre-line' }}>{fehler}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={laeuft}>Schließen</button>
          {vorschau?.moeglich && (
            <button className="btn-primary" onClick={erstellen} disabled={laeuft || !!eingabeFehler}>
              {laeuft ? 'Wird erstellt ...' : 'Nachtragsrechnung erstellen'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
